/**
 * Rebinding persistence glue between `Settings.bindings` (persisted with the rest of the settings under
 * `hf.settings.v1`) and the InputManager. Pure helpers + one apply function; unit-tested in settings-apply.test.ts.
 */
import { BUTTON_NAMES, type ButtonName } from '@hf/shared';
import { bindingsEqual, cloneBindings, sanitizeBindings } from '../input/bindings';
import { DEVICE_ID, padIndexOf, type Bindings, type InputManager } from '../input/types';

/**
 * Push the saved overrides into the manager (untrusted storage is sanitized). Uses `InputManager.loadBindings`
 * (which also returns unlisted devices to their defaults) and falls back to per-device `setBindings`.
 */
export const applySavedBindings = (
  input: InputManager,
  saved: Record<string, Bindings> | undefined,
): void => {
  const load = (input as Partial<InputManager>).loadBindings;
  if (typeof load === 'function') {
    load.call(input, saved && typeof saved === 'object' ? saved : {});
    return;
  }
  if (!saved || typeof saved !== 'object') return;
  for (const [deviceId, raw] of Object.entries(saved)) {
    if (deviceId === DEVICE_ID.TOUCH) continue;
    try {
      input.setBindings(deviceId, sanitizeBindings(deviceId, raw));
    } catch {
      /* a device the manager does not know: ignore */
    }
  }
};

/**
 * Rebind one logical button on a device and return the next `Settings.bindings` record. Defaults are stored as
 * "no override" so a later change of factory defaults reaches the player.
 */
export const withRebound = (
  saved: Record<string, Bindings>,
  deviceId: string,
  current: Bindings,
  defaults: Bindings,
  button: ButtonName,
  code: string,
): { next: Record<string, Bindings>; bindings: Bindings; moved: RebindMove | null } => {
  const { bindings, moved } = rebindPrimary(current, defaults, button, code, isPadDevice(deviceId));
  return { next: withOverride(saved, deviceId, bindings, defaults), bindings, moved };
};

/** What a rebind did to ANOTHER button (the one that owned the captured code). */
export interface RebindMove {
  button: ButtonName;
  /** The captured code it lost. */
  lost: string;
  /** The code it got instead (it would have been left with none), or null when it kept other codes / is unbound. */
  now: string | null;
  /** It has no code left at all. */
  unbound: boolean;
}

/** Pad D-pad / left-stick directions: movement inputs, never a button's "primary" (and never stripped). */
const isPadDirection = (code: string): boolean => /^(?:1[2-5]|axis[01][-+])$/.test(code);

/**
 * Settings rebind (QA r4): `code` replaces ONLY the button's primary code (the first one; on a pad the first that is
 * not a D-pad / stick direction). Its alternates stay, so rebinding JUMP to J on the 1-player keyboard keeps ↑ and
 * Space jumping (the old rebind dropped every other key and silently broke the arrow layout). A key the player
 * picks that already belongs to the button is just promoted.
 *
 * If another button owned `code`, it loses it, and only when that leaves it with NO code does it get a replacement:
 * one of its own factory defaults that is free, else the rebound button's old primary (a classic swap: P1 ACTION →
 * D makes MOVE RIGHT J). It never silently inherits a key it does not need: JUMP → RB on a pad used to hand the A
 * button to SPECIAL, so a jump press fired the special and wasted a full meter. `moved` reports the other button
 * for the toast and the row highlight. Pure.
 */
export const rebindPrimary = (
  current: Bindings,
  defaults: Bindings,
  button: ButtonName,
  code: string,
  pad: boolean,
): { bindings: Bindings; moved: RebindMove | null } => {
  const next = cloneBindings(current);
  const before = current[button];
  let freed: string | null = null;
  if (before.includes(code)) next[button] = [code, ...before.filter((c) => c !== code)];
  else {
    const pi = pad ? before.findIndex((c) => !isPadDirection(c)) : before.length > 0 ? 0 : -1;
    freed = pi >= 0 ? before[pi]! : null;
    next[button] = [code, ...before.filter((_, i) => i !== pi)].slice(0, MAX_CODES);
  }
  let moved: RebindMove | null = null;
  for (const name of BUTTON_NAMES) {
    if (name === button || !next[name].includes(code)) continue;
    next[name] = next[name].filter((c) => c !== code);
    if (next[name].length > 0) {
      moved = { button: name, lost: code, now: null, unbound: false };
      continue;
    }
    const used = new Set(BUTTON_NAMES.flatMap((n) => next[n]));
    const pick =
      defaults[name].find((c) => !used.has(c)) ?? (freed !== null && !used.has(freed) ? freed : null);
    if (pick !== null) next[name] = [pick];
    moved = { button: name, lost: code, now: pick, unbound: pick === null };
  }
  return { bindings: next, moved };
};

const MAX_CODES = 6;

export const withOverride = (
  saved: Record<string, Bindings>,
  deviceId: string,
  bindings: Bindings,
  defaults: Bindings,
): Record<string, Bindings> => {
  const next = { ...saved };
  if (bindingsEqual(bindings, defaults)) delete next[deviceId];
  else next[deviceId] = bindings;
  return next;
};

/** Logical buttons in rebinding order with their on-screen names. */
export const BUTTON_ROWS: readonly { button: ButtonName; name: string; hint: string }[] = [
  { button: 'L', name: 'MOVE LEFT', hint: 'Double-tap = dash' },
  { button: 'R', name: 'MOVE RIGHT', hint: 'Double-tap = dash' },
  { button: 'U', name: 'JUMP', hint: 'Hurdle · hard count pre-snap' },
  { button: 'D', name: 'DOWN', hint: 'Truck · fake-jump cancel · FG formation' },
  { button: 'A', name: 'ACTION', hint: 'Snap · throw · tackle · stiff-arm' },
  { button: 'S', name: 'SPECIAL', hint: 'Signature special at full power' },
];
// Keep the table in sync with the input bits.
if (BUTTON_ROWS.length !== BUTTON_NAMES.length) throw new Error('BUTTON_ROWS out of sync with BUTTON_NAMES');

/** Rebindable devices (touch has a fixed layout). Pads only while connected. */
export const rebindableDevices = (input: InputManager): { id: string; label: string }[] => {
  const out: { id: string; label: string }[] = [
    { id: DEVICE_ID.KB_SOLO, label: 'KEYBOARD · 1 PLAYER' },
    { id: DEVICE_ID.KB_P1, label: 'KEYBOARD · P1 (LEFT)' },
    { id: DEVICE_ID.KB_P2, label: 'KEYBOARD · P2 (RIGHT)' },
  ];
  for (const d of input.devices()) {
    const pi = padIndexOf(d.id);
    if (pi >= 0 && d.connected) out.push({ id: d.id, label: `PAD ${pi + 1} · ${shortPadName(d.label)}` });
  }
  return out;
};

const shortPadName = (label: string): string => {
  const clean = label
    .replace(/\(.*?\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  return clean.length > 26 ? `${clean.slice(0, 25)}…` : clean || 'GAMEPAD';
};

/** Is the device a pad (capture listens to buttons/sticks instead of keys)? */
export const isPadDevice = (deviceId: string): boolean => padIndexOf(deviceId) >= 0;
